/**
 * lig-ornament-maker.js — "Draw my dog" (Horizon module).
 *
 * Flow: photo → downscale in the browser → {middleware}/api/custom-design/upload
 * → POST create on {middleware}/api/ornament/design → poll until ready →
 * preview → Add to cart with `_design_id` (+ a visible Dog / Design / Shape).
 *
 * The middleware never sees a stranger's URL (only its own Blob), never hands
 * back the print file (preview only), and refuses to route an ornament line
 * that arrives without `_design_id` — which is why there is no express-checkout
 * button here: it drops line-item properties.
 *
 * Cart add mirrors lig-bundle-builder.js: /cart/add.js with section ids, then
 * CartAddEvent so the drawer morphs, then drawer.open() because the event
 * alone does not open it.
 *
 * NOTHING IS LOST: every drawing this browser makes goes into a tray
 * (localStorage ids → the public design view), so another scene, another
 * photo, a refresh or a closed window never throws a drawing away; tap one
 * in the tray to bring it back and add it to the cart.
 */
import {makerMetrics} from './lb-metrics.js';
import { CartAddEvent, CartErrorEvent } from '@theme/events';

const DOWNSCALE_MAX_EDGE = 2000;
const DOWNSCALE_QUALITY = 0.86;
const POLL_MS = 2500;
const POLL_MAX_MS = 8 * 60 * 1000; // a cold function + a slow image service can take past five
const SHAPE_PATHS = {
  circle: 'M120,16 A104,104 0 1 1 119.9,16 Z',
  heart: 'M120 216 C48 163 14 129 14 86 A46 46 0 0 1 120 62 A46 46 0 0 1 226 86 C226 129 192 163 120 216 Z',
  star: 'M120,14 L148.3,85 L225,90.5 L165.6,139.5 L184.8,214 L120,172.8 L55.2,214 L74.4,139.5 L15,90.5 L91.7,85 Z',
  snowflake: 'M120,16 A104,104 0 1 1 119.9,16 Z',
};

class OrnamentMaker {
  constructor(root) {
    this.root = root;
    this.track=makerMetrics('ornament','https://lig-fulfillment-middleware.vercel.app/api/maker-events');
    this.mw = (root.dataset.middleware || '').replace(/\/+$/, '');
    const q = (s) => root.querySelector(s);
    this.el = {
      file: q('[data-om-file]'), drop: q('[data-om-drop]'), dropText: q('[data-om-drop-text]'), thumb: q('[data-om-thumb]'),
      templates: q('[data-om-templates]'), shapes: q('[data-om-shapes]'), name: q('[data-om-name]'), line2: q('[data-om-line2]'),
      line2Field: q('[data-om-line2-field]'), line2Label: q('[data-om-line2-label]'), form: q('[data-om-form]'), draw: q('[data-om-draw]'),
      drawNote: q('[data-om-draw-note]'), result: q('[data-om-result]'), add: q('[data-om-add]'), retry: q('[data-om-retry]'), retitle: q('[data-om-retitle]'),
      qty: q('[data-om-qty]'), status: q('[data-om-status]'), preview: q('[data-om-preview]'), placeholder: q('[data-om-placeholder]'),
      placeholderName: q('[data-om-placeholder-name]'), shapePath: q('[data-om-shape]'), drawing: q('[data-om-drawing]'), drawingText: q('[data-om-drawing-text]'),
      handoff: q('[data-om-handoff]'), hoEmail: q('[data-om-ho-email]'), hoNote: q('[data-om-ho-note]'), hoSend: q('[data-om-ho-send]'), hoStatus: q('[data-om-ho-status]'),
      top: q('[data-om-top]'), topField: q('[data-om-top-field]'), topLabel: q('[data-om-top-label]'), styles: q('[data-om-styles]'), stylesField: q('[data-om-styles-field]'), wordsTip: q('[data-om-words-tip]'),
      save: q('[data-om-save]'), saveEmail: q('[data-om-save-email]'), saveSend: q('[data-om-save-send]'), saveStatus: q('[data-om-save-status]'),
    };
    try { this.product = JSON.parse(root.querySelector('[data-om-product]')?.textContent || 'null'); } catch { this.product = null; }
    this.el.stageTop = q('[data-om-stage]'); this.el.sample = q('[data-om-sample]'); this.el.summary = q('[data-om-summary]');
    this.el.tray = q('[data-om-tray]'); this.el.trayList = q('[data-om-tray-list]');
    this.privateIntake = false; this.pollEpoch = 0;
    this.manifest = null;
    // `designs` is the tray: every drawing this browser has made (newest
    // first), kept in localStorage — closing the window, or clicking another
    // scene, never loses one (Mike lost Enzo's that way on 23 Sep 2026).
    this.state = { photoUrl: '', template: '', shape: '', design: null, designs: [], pollTimer: null, typedSinceDraw: false, line2Default: '', topDefault: '', style: '', styleTouched: false, free: false, saveEmail: false, restoring: false, composing: false, cartBusy: false, uploading: false };
    this.lightboxOn = root.dataset.lightbox !== 'false';
    this.trayOn = root.dataset.tray !== 'false';
    this.init();
  }

  async init() {
    if (!this.product) return;
    this.bind();
    try {
      const r = await fetch(`${this.mw}/api/ornament/design`, { cache: 'no-store' });
      this.manifest = await r.json();
      if (!r.ok || !Array.isArray(this.manifest.templates) || !this.manifest.templates.length) throw Error('Unavailable ornament scenes.');
    } catch {
      this.status('The ornament maker is having trouble loading — please refresh.', true);
      return;
    }
    if(this.root.dataset.sceneBase)for(const t of this.manifest.templates)if(!t.example_url?.includes('measured-lettering-'))t.example_url=this.root.dataset.sceneBase.replace('om-scene-truck-320.webp',`om-scene-${t.id}-800.webp`);
    this.state.free = !!this.manifest.free_words;
    this.state.saveEmail = !!this.manifest.save_email;
    this.renderStyles();
    this.renderTemplates();
    this.pick(this.manifest.templates[0].id);
    window.LigOrnamentMaker = { openScene: (id) => this.openScene(id), pick: (id) => this.pick(id), browse: () => this.browseScenes() };
    let scene = ''; try { scene = sessionStorage.getItem('lig_om_pick') || ''; sessionStorage.removeItem('lig_om_pick'); } catch {}
    if (scene) this.pick(scene);
    // A link from the "email me my drawing" note brings that drawing back first.
    let wanted = '';
    try { wanted = new URLSearchParams(location.search).get('design') || ''; } catch { /* ignore */ }
    if (/^orn_[a-z0-9]{6,}$/.test(wanted)) {
      try {
        const r = await fetch(`${this.mw}/api/ornament/design?id=${encodeURIComponent(wanted)}`, { cache: 'no-store' });
        const data = await r.json();
        if (r.ok && data.design) { this.remember(data.design); try { sessionStorage.setItem('lig_om_design', data.design.id); } catch { /* ignore */ } }
      } catch { /* a dead link is just a fresh start */ }
    }
    // Every drawing this browser made comes back — the most recent one as
    // the current drawing, the rest in the tray.
    const fromCalendar = new URLSearchParams(location.search).get('from') === 'calendar';
    this.privateIntake = fromCalendar;
    if (fromCalendar) { const back = document.createElement('a'); back.href = '/pages/your-dog-calendar'; back.textContent = '← Back to your saved calendar'; back.style.cssText = 'display:inline-block;margin:0 0 20px;color:inherit'; this.root.querySelector('.lig-om__wrap').prepend(back); }
    const handoff = fromCalendar ? window.LigDogHandoff?.consume('ornament') : null;
    if (handoff) {
      this.privateIntake = true;
      this.el.name.value = handoff.name;
      this.pick(handoff.scene);
      this.root.scrollIntoView({ behavior: 'smooth', block: 'start' });
      try {
        await this.onPhoto(window.LigDogHandoff.toFile(handoff.photo));
        if (this.state.photoUrl) await this.draw();
      } catch (error) { this.status(error.message || 'Please choose your photo again.', true); }
    } else if (this.trayOn) await this.restoreTray(wanted);
    else { let saved = ''; try { saved = sessionStorage.getItem('lig_om_design') || ''; } catch { /* ignore */ } if (saved) this.resume(saved); }
  }

  /** The words as typed: the three lines when words are free, else the name and the scene's line. */
  words() {
    const name = this.el.name.value.trim();
    const bottom = this.el.line2 && !this.el.line2Field.hidden ? this.el.line2.value.trim() : '';
    if (!this.state.free) return { name, bottom };
    const top = this.el.top && !this.el.topField.hidden ? this.el.top.value.trim() : '';
    return { top, name, bottom };
  }
  wordsBody() {
    const w = this.words();
    return { name: w.name, line2: w.bottom, ...(this.state.free ? { words: w, style: this.state.style } : {}) };
  }

  renderStyles() {
    if (!this.el.styles) return;
    const styles = Array.isArray(this.manifest.styles) ? this.manifest.styles : [];
    const scene=this.manifest.templates.find(t=>t.id===this.state.template),recommended=scene?.style||'classic';
    const offered=[...new Set([recommended,'classic','script',this.state.style].filter(Boolean))];
    this.el.styles.innerHTML = offered.map(id=>styles.find(st=>st.id===id)).filter(Boolean).map(st=>`<button type="button" class="lig-om__chip lig-om__style" role="radio" aria-checked="${this.state.style===st.id}" data-style="${esc(st.id)}"><span class="lig-om__type-sample" data-type-face="${st.id==='script'||st.id==='classic'&&scene?.name_font==='script'?'script':'serif'}">${esc(this.el.name.value.trim()||'Your dog')}</span><span>${esc(st.label)}</span><small>${st.id===recommended?'Recommended for this scene':'Alternative lettering'}</small></button>`).join('');
    this.el.styles.querySelectorAll('[data-style]').forEach((b) => b.addEventListener('click', () => this.pickStyle(b.dataset.style, false)));
  }
  pickStyle(id, silent) {
    const styles = Array.isArray(this.manifest.styles) ? this.manifest.styles : [];
    const st = styles.find((x) => x.id === id) || styles[0];
    if (!st) return;
    this.state.style = st.id;
    if (!silent) this.state.styleTouched = true;
    if (this.el.styles) this.el.styles.querySelectorAll('[data-style]').forEach((b) => b.setAttribute('aria-checked', String(b.dataset.style === st.id)));
    this.renderStyles();
    this.applySlots();
    if (!silent && !this.state.restoring && this.state.design && this.state.design.status === 'ready' && (this.state.composing || this.state.design.style !== st.id)) this.recompose({ style: st.id });
  }
  /** Which fields show: the style says whether it carries a line above / below; without free words, only the scene's own line. */
  applySlots() {
    const t = this.manifest.templates.find((x) => x.id === this.state.template);
    const styles = Array.isArray(this.manifest.styles) ? this.manifest.styles : [];
    const st = styles.find((x) => x.id === this.state.style);
    if (this.el.stylesField) this.el.stylesField.hidden = !this.state.free || !styles.length;
    if (this.el.wordsTip) this.el.wordsTip.hidden = !this.state.free;
    if (this.el.topField) this.el.topField.hidden = !(this.state.free && st && st.top);
    if (this.el.line2Field) this.el.line2Field.hidden = this.state.free ? !(st && st.bottom) : !(t && t.line2);
    const arched=st?.id==='arch';
    if(this.el.topLabel)this.el.topLabel.textContent=arched?'Curved message across the top (optional)':'Message above the name (optional)';
    const help=this.root.querySelector('[data-om-top-help]');if(help)help.textContent=arched?'This follows the ornament’s upper curve. Leave it blank for no top message.':'This sits above the name in the selected lettering layout. Leave it blank to omit it.';
    if(this.el.line2Label)this.el.line2Label.textContent='Line below the name (optional)';
    if(this.el.line2)this.el.line2.placeholder='e.g. 2026 or Hingham, MA';
    this.wordSummary();
  }

  wordSummary() {
    const el=this.root.querySelector('[data-om-word-summary]');if(!el)return;
    const w=this.words();el.innerHTML=`<strong>Your ornament’s words</strong><span>${w.top?esc(w.top):'No top message'}</span><b>${esc(w.name||'Your dog’s name')}</b><span>${w.bottom?esc(w.bottom):'No lower line'}</span><small>Text summary — your finished preview shows the placement.</small>`;
  }

  /** The scene browser: one scene large, arrows through all of them, "Use this scene". */
  lightbox() {
    if (this.lb) return this.lb;
    const d = document.createElement('dialog');
    d.className = 'lig-om__lightbox';
    d.innerHTML = `<button type="button" class="lig-om__lb-close" data-lb-close aria-label="Close">×</button><button type="button" class="lig-om__lb-arrow lig-om__lb-arrow--prev" data-lb-prev aria-label="Previous scene">‹</button><figure class="lig-om__lb-figure"><img data-lb-img alt="" /><figcaption><p class="lig-om__lb-occasion" data-lb-occasion></p><h3 data-lb-title></h3><p data-lb-blurb></p><p class="lig-om__lb-count" data-lb-count></p><div class="lig-om__lb-actions"><button type="button" class="lig-om__btn lig-om__btn--primary" data-lb-use>Use this scene</button></div></figcaption></figure><button type="button" class="lig-om__lb-arrow lig-om__lb-arrow--next" data-lb-next aria-label="Next scene">›</button>`;
    document.body.appendChild(d);
    d.querySelector('[data-lb-close]').addEventListener('click', () => d.close());
    d.querySelector('[data-lb-prev]').addEventListener('click', () => this.stepScene(-1));
    d.querySelector('[data-lb-next]').addEventListener('click', () => this.stepScene(1));
    d.querySelector('[data-lb-use]').addEventListener('click', () => { const id = this.lbId; d.close(); this.pick(id); this.el.form.scrollIntoView({ behavior: 'smooth', block: 'start' }); this.el.name.focus({preventScroll: true}); });
    d.addEventListener('click', (ev) => { if (ev.target === d) d.close(); });
    d.addEventListener('keydown', (ev) => { if (ev.key === 'ArrowLeft') this.stepScene(-1); if (ev.key === 'ArrowRight') this.stepScene(1); });
    let x0 = null;
    d.addEventListener('touchstart', (ev) => { x0 = ev.touches[0].clientX; }, { passive: true });
    d.addEventListener('touchend', (ev) => { if (x0 === null) return; const dx = ev.changedTouches[0].clientX - x0; x0 = null; if (Math.abs(dx) > 40) this.stepScene(dx < 0 ? 1 : -1); }, { passive: true });
    this.lb = d;
    return d;
  }
  sceneOrder() {
    const byId = new Map(this.manifest.templates.map((t) => [t.id, t]));
    const occ = Array.isArray(this.manifest.occasions) ? this.manifest.occasions : [];
    const ordered = occ.flatMap((o) => (o.scenes || []).map((id) => byId.get(id)).filter(Boolean));
    return ordered.length ? ordered : this.manifest.templates;
  }
  openScene(id) {
    if (!this.lightboxOn || !this.manifest) return;
    const order = this.sceneOrder();
    const t = order.find((x) => x.id === id) || order[0];
    if (!t) return;
    this.lbId = t.id;
    const d = this.lightbox();
    const occ = (this.manifest.occasions || []).find((o) => (o.scenes || []).includes(t.id));
    d.querySelector('[data-lb-img]').src = t.example_url || '';
    d.querySelector('[data-lb-img]').alt = `${t.label} — illustrated scene sample`;
    d.querySelector('[data-lb-title]').textContent = t.label;
    d.querySelector('[data-lb-blurb]').textContent = t.blurb || '';
    d.querySelector('[data-lb-occasion]').textContent = occ ? occ.label : '';
    d.querySelector('[data-lb-count]').textContent = `${order.indexOf(t) + 1} of ${order.length}`;
    if (!d.open) d.showModal();
    d.querySelector('[data-lb-next]').focus();
  }
  stepScene(dir) {
    const order = this.sceneOrder();
    const i = order.findIndex((x) => x.id === this.lbId);
    const next = order[(i + dir + order.length) % order.length];
    if (next) this.openScene(next.id);
  }

  async save() {
    const d = this.state.design;
    const email = (this.el.saveEmail.value || '').trim();
    if (!d || d.status !== 'ready') { this.el.saveStatus.textContent = 'Draw it first — then we can send you a link to it.'; return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email)) { this.el.saveStatus.textContent = 'That email does not look right.'; return; }
    this.el.saveSend.disabled = true;
    this.el.saveStatus.textContent = 'Sending…';
    try {
      const r = await this.api({ action: 'save', id: d.id, email });
      if(r.design)this.state.design=r.design;this.track('save');this.el.saveStatus.textContent = r.message || 'It is on its way.';
    } catch (err) { this.el.saveStatus.textContent = err.message; }
    this.el.saveSend.disabled = false;
  }

  rememberedIds() {
    try { const ids = JSON.parse(localStorage.getItem('lig_om_designs_v1') || '[]'); return Array.isArray(ids) ? ids.filter((x) => typeof x === 'string') : []; } catch { return []; }
  }
  remember(design) {
    if (!design || !design.id) return;
    this.state.designs = [design, ...this.state.designs.filter((d) => d.id !== design.id)];
    try { localStorage.setItem('lig_om_designs_v1', JSON.stringify([...new Set([...this.state.designs.map((d) => d.id), ...this.rememberedIds()])])); } catch { /* storage blocked */ }
    try { sessionStorage.setItem('lig_om_design', design.id); } catch { /* ignore */ }
    this.renderTray();
  }
  async restoreTray(wanted = '') {
    const epoch = this.pollEpoch;
    const ids = this.rememberedIds();
    let current = '';
    try { current = sessionStorage.getItem('lig_om_design') || ''; } catch { /* ignore */ }
    const found = [];
    // Restore in bounded parallel batches; never rewrite the saved ID list from partial results.
    for (let offset = 0; offset < ids.length; offset += 4) {
      const batch = await Promise.all(ids.slice(offset, offset + 4).map(async id => {
        try { const r = await fetch(`${this.mw}/api/ornament/design?id=${encodeURIComponent(id)}`, {cache:'no-store'});
          const data = await r.json(); return r.ok ? data.design : null;
        } catch { return null; }
      }));
      found.push(...batch.filter(Boolean));
    }
    this.state.designs = found;
    this.renderTray();
    const first = wanted ? found.find((d) => d.id === wanted) : null;
    if (first && epoch === this.pollEpoch) this.use(first);
  }
  renderTray() {
    if (!this.el.tray || !this.trayOn) return;
    const ds = this.state.designs.filter((d) => d.preview_url);
    this.el.tray.hidden = ds.length === 0;
    const cur = this.state.design && this.state.design.id;
    this.el.trayList.innerHTML = ds.map((d) => {
      const t = this.manifest.templates.find((x) => x.id === d.template) || { label: d.template };
      return `<button type="button" class="lig-om__tray-item" data-om-use="${esc(d.id)}" aria-current="${d.id === cur ? 'true' : 'false'}" title="${esc(d.name)} — ${esc(t.label)}"><img src="${esc(d.preview_url)}" alt="" loading="lazy" /><span>${esc(d.name)}<small>${esc(t.label)}</small></span></button>`;
    }).join('');
    this.el.trayList.querySelectorAll('[data-om-use]').forEach((b) => b.addEventListener('click', () => { const d = this.state.designs.find((x) => x.id === b.dataset.omUse); if (d) this.use(d); }));
  }
  /** Bring a drawing back as the current one — scene, shape, name and all. */
  use(design) {
    if (this.state.composing || this.state.cartBusy) return;
    this.pollEpoch++;
    if (this.state.pollTimer) clearTimeout(this.state.pollTimer);
    this.track('resume');
    this.state.restoring = true;
    try {
      this.state.design = design;
      const t = this.manifest.templates.find((x) => x.id === design.template);
      if (t) { this.pick(t.id); this.pickShape(design.shape); }
      const dw = design.words || { top: '', name: design.name, bottom: design.line2 || '' };
      this.el.name.value = dw.name || design.name;
      if (this.el.line2) { this.el.line2.value = dw.bottom || ''; this.state.line2Default = this.el.line2.value; }
      if (this.el.top) { this.el.top.value = dw.top || ''; this.state.topDefault = this.el.top.value; }
      if (this.state.free && design.style) { this.pickStyle(design.style, true); this.state.styleTouched = !!(t && design.style !== t.style); }
      this.el.placeholderName.textContent = dw.name || design.name;
      if (design.status === 'ready') this.showReady(design);
      else if (design.status === 'queued' || design.status === 'drawing') { this.showDrawing('Still drawing…'); this.poll(Date.now()); }
    } finally { this.state.restoring = false; }
    this.renderTray();
  }
  /** Set the current drawing aside (it stays in the tray) and show an empty stage. */
  park() {
    this.pollEpoch++;
    if (this.state.pollTimer) clearTimeout(this.state.pollTimer);
    this.el.draw.disabled = false; this.el.retry.disabled = false; this.hideDrawing();
    if (!this.state.design) return;
    if (this.state.pollTimer) clearTimeout(this.state.pollTimer);
    this.state.design = null;
    this.el.preview.hidden = true; this.el.preview.removeAttribute('src');
    this.el.placeholder.hidden = false;
    this.el.result.hidden = true;
    this.el.retitle.hidden = true;
    this.el.draw.disabled = false; this.el.draw.textContent = this.el.draw.dataset.label || this.el.draw.textContent;
    try { sessionStorage.removeItem('lig_om_design'); } catch { /* ignore */ }
    this.renderTray();
  }

  bind() {
    const { el } = this;
    el.draw.dataset.label = el.draw.textContent;
    el.file.addEventListener('change', () => this.onPhoto(el.file.files && el.file.files[0]));
    el.form.addEventListener('submit', (ev) => { ev.preventDefault(); if (this.state.design?.status === 'ready') { el.file.click(); return; } this.draw(); });
    this.root.querySelector('[data-om-browse]').addEventListener('click', () => this.browseScenes());
    el.qty.addEventListener('input', () => this.syncPurchase());
    el.retry.addEventListener('click', () => this.draw(true));
    el.retitle.addEventListener('click', () => this.recompose());this.root.querySelector('[data-om-word-update]')?.addEventListener('click',()=>this.recompose());
    el.add.addEventListener('click', () => this.addToCart());
    el.hoSend.addEventListener('click', () => this.handoff());
    const onType = () => {
      this.syncPurchase();this.renderStyles();this.wordSummary();
      el.placeholderName.textContent = el.name.value.trim() || 'Their name';
      if (this.state.design && this.state.design.status === 'ready') {
        const w = this.words(), dw = this.state.design.words || { top: '', name: this.state.design.name, bottom: this.state.design.line2 || '' };
        const changed = w.name !== dw.name || (w.bottom || '') !== (dw.bottom || '') || (this.state.free && (w.top || '') !== (dw.top || ''));
        el.retitle.hidden = !changed;
      }
    };
    el.name.addEventListener('input', onType);
    if (el.line2) el.line2.addEventListener('input', onType);
    if (el.top) el.top.addEventListener('input', onType);
    if (el.saveSend) el.saveSend.addEventListener('click', () => this.save());
    ['dragenter', 'dragover'].forEach((t) => el.drop.addEventListener(t, (ev) => { ev.preventDefault(); el.drop.classList.add('is-busy'); }));
    ['dragleave', 'drop'].forEach((t) => el.drop.addEventListener(t, (ev) => { ev.preventDefault(); el.drop.classList.remove('is-busy'); }));
    el.drop.addEventListener('drop', (ev) => { const f = ev.dataTransfer && ev.dataTransfer.files && ev.dataTransfer.files[0]; if (f) this.onPhoto(f); });
  }

  status(text, isError = false) {
    this.el.status.textContent = text || '';
    this.el.status.classList.toggle('is-error', !!isError);
  }

  thumbnail(t) { if(t.example_url?.includes('measured-lettering-'))return t.example_url;return this.root.dataset.sceneBase ? this.root.dataset.sceneBase.replace('om-scene-truck-320.webp', `om-scene-${t.id}-320.webp`) : t.example_url; }
  renderTemplates() {
    const featured = ['truck','portrait','gotcha','bridge','lighthouse','christmas_morning'];
    const choices = featured.map(id => this.manifest.templates.find(t => t.id === id)).filter(Boolean);
    this.el.templates.innerHTML = choices.map(t => `<button type="button" class="lig-om__tpl" aria-pressed="${this.state.template===t.id}" data-tpl="${esc(t.id)}"><img src="${esc(this.thumbnail(t))}" alt="" width="160" height="160" loading="lazy"><b>${esc(t.label)}</b></button>`).join('');
    this.el.templates.querySelectorAll('[data-tpl]').forEach(b => b.onclick=()=>this.pick(b.dataset.tpl));
  }
  browseScenes() {
    if (!this.browserDialog) {
      const d = document.createElement('dialog'); d.className='lig-om__lightbox lig-om__browser';
      d.innerHTML='<button type="button" class="lig-om__lb-close" aria-label="Close scene browser">×</button><h2>Find their scene</h2><label>Occasion <select data-filter><option value="">All scenes</option></select></label><div class="lig-om__browse-grid"></div>';
      document.body.append(d); d.querySelector('button').onclick=()=>d.close();
      for(const o of this.manifest.occasions || []) { const option=document.createElement('option'); option.value=o.label; option.textContent=o.label; d.querySelector('select').append(option); }
      const render=()=>{const selected=(this.manifest.occasions||[]).find(o=>o.label===d.querySelector('select').value); const scenes=this.manifest.templates.filter(t=>!selected||selected.scenes.includes(t.id));
        d.querySelector('.lig-om__browse-grid').innerHTML=scenes.map(t=>`<button type="button" class="lig-om__tpl" data-scene="${esc(t.id)}"><img src="${esc(this.thumbnail(t))}" width="160" height="160" alt="" loading="lazy"><b>${esc(t.label)}</b><small>See scene →</small></button>`).join('');
        d.querySelectorAll('[data-scene]').forEach(b=>b.onclick=()=>{d.close();this.openScene(b.dataset.scene);});};
      d.querySelector('select').onchange=render; render(); this.browserDialog=d;
    }
    this.browserDialog.showModal();
  }
  dirty() {
    const d=this.state.design; if(!d)return false;
    const w=this.words(), dw=d.words||{name:d.name,bottom:d.line2||'',top:''};
    return w.name!==dw.name || (w.bottom||'')!==(dw.bottom||'') || (this.state.free && ((w.top||'')!==(dw.top||'') || this.state.style!==d.style)) || this.state.shape!==d.shape;
  }
  syncPurchase() {
    const d=this.state.design, dirty=this.dirty(), busy=this.state.composing||this.state.cartBusy||this.state.uploading;
    this.el.add.disabled=!d||d.status!=='ready'||dirty||busy||d.editable===false;
    this.el.retitle.hidden=!dirty; this.el.retitle.disabled=busy||d?.editable===false;const wordUpdate=this.root.querySelector('[data-om-word-update]');if(wordUpdate){wordUpdate.hidden=!dirty||d?.status!=='ready';wordUpdate.disabled=busy||d?.editable===false;}
    if(this.el.summary && d) {
      const w=d.words||{name:d.name,bottom:d.line2}; const qty=Math.max(1,Math.min(10,Number(this.el.qty.value)||1));
      const price=new Intl.NumberFormat(document.documentElement.lang||'en-US',{style:'currency',currency:this.product.currency||'USD'}).format(this.product.price*qty/100);
      this.el.summary.textContent=dirty?'You have changes to apply. Update the preview before adding to cart.':`${[w.top,w.name,w.bottom].filter(Boolean).join(' · ')} · ${d.shape} · ${qty} × ornament · ${price}. Shipping calculated at checkout. Made to order; allow about a week before dispatch, plus delivery.`;
    }
  }

  pick(templateId) {
    const t = this.manifest.templates.find((x) => x.id === templateId);
    if (!t) return;
    if (this.state.cartBusy || this.state.composing) return;
    if (!this.state.restoring && (this.state.design ? this.state.design.template !== t.id : this.state.template && this.state.template !== t.id)) this.park();
    this.track('scene',{scene:t.id});
    this.state.template = t.id;
    const selected=this.root.querySelector('[data-om-selected]'); if(selected) selected.textContent=`Selected: ${t.label}`;
    if (!this.state.design && this.el.sample) { this.el.sample.src=t.example_url||''; this.el.sample.hidden=!t.example_url; this.el.placeholder.hidden=!!t.example_url; this.status('Scene sample — your own dog and words appear after you draw.'); }
    this.el.templates.querySelectorAll('[data-tpl]').forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.tpl === t.id)));
    // The scene prefills the words; whatever the customer typed themselves stays.
    const tw = t.words || { top: '', bottom: t.line2 ? (t.line2.default || '') : '', top_label: 'Small line above (optional)', bottom_label: (t.line2 && t.line2.label) || 'Line below (optional)', bottom_placeholder: (t.line2 && t.line2.placeholder) || '' };
    if (this.el.line2) {
      const bottomDefault = this.state.free ? (tw.bottom || '') : (t.line2 ? (t.line2.default || '') : '');
      this.el.line2Label.textContent = this.state.free ? tw.bottom_label : ((t.line2 && t.line2.label) || 'Line below (optional)');
      this.el.line2.placeholder = this.state.free ? (tw.bottom_placeholder || '') : ((t.line2 && t.line2.placeholder) || '');
      const cur = this.el.line2.value.trim();
      if (!cur || cur === this.state.line2Default) this.el.line2.value = bottomDefault;
      this.state.line2Default = bottomDefault;
    }
    if (this.state.free && this.el.top) {
      const curT = this.el.top.value.trim();
      if (!curT || curT === this.state.topDefault) this.el.top.value = tw.top || '';
      this.state.topDefault = tw.top || '';
      this.el.topLabel.textContent = tw.top_label || 'Small line above (optional)';
    }
    if (this.state.free && !this.state.styleTouched) this.pickStyle(t.style || 'classic', true);
    this.applySlots();
    const shapes = t.shapes.filter((s) => this.variantFor(s));
    // A new scene uses the same shape as its pictured example. Reopening a
    // saved drawing retains its actual shape, including older heart designs.
    this.pickShape(this.state.design?.template===t.id?this.state.design.shape:(shapes.includes(t.default_shape)?t.default_shape:shapes[0]));
    this.renderStyles();
    // Another scene means a new drawing — the one on the stage goes to the tray, never away.
    if (!this.state.restoring && this.state.design && this.state.design.template !== t.id) this.park();
  }

  pickShape(shape) {
    this.state.shape = shape;
    const note=this.root.querySelector('[data-om-shape-note]');if(note)note.textContent=`${(this.manifest.shapes.find(s=>s.key===shape)||{label:shape}).label} ornament · the shape shown for this scene.`;
    this.applySlots();
    this.el.shapes.querySelectorAll('[data-shape]').forEach((b) => b.setAttribute('aria-checked', String(b.dataset.shape === shape)));
    this.el.shapePath.setAttribute('d', SHAPE_PATHS[shape] || SHAPE_PATHS.circle);
    if (!this.state.restoring && this.state.design && this.state.design.status === 'ready' && (this.state.composing || this.state.design.shape !== shape)) this.recompose({ shape });
  }

  variantFor(shape) {
    const v = (this.product.variants || []).find((x) => String(x.title).trim().toLowerCase() === shape);
    return v && v.available !== false ? v : null;
  }

  async onPhoto(file) {
    if (!file || this.state.cartBusy || this.state.composing || this.state.uploading) return;
    this.park(); this.state.photoUrl = '';
    this.state.uploading=true; this.syncPurchase(); this.el.draw.disabled=true;
    const uploadStarted=performance.now();
    this.el.drop.classList.add('is-busy');
    this.el.dropText.textContent = 'Uploading…';
    try {
      const prepared = await downscale(file);
      if (!prepared) throw new Error('That photo is too large — try a smaller one.');
      const fname = (file.name || 'dog').replace(/\.[^.]+$/, '') + '.' + prepared.ext;
      const res = await fetch(`${this.mw}/api/custom-design/upload?filename=${encodeURIComponent(fname)}${this.privateIntake ? '&private=1' : ''}`, { method: 'POST', headers: { 'Content-Type': prepared.contentType }, body: prepared.blob });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.url) throw new Error(data.error || `upload failed (${res.status})`);
      this.track('upload',{ms:performance.now()-uploadStarted,format:/hei[cf]/i.test(file.name+file.type)?'heic':prepared.ext==='png'?'png':'jpeg'});
      this.state.photoUrl = data.url;
      this.el.thumb.src = URL.createObjectURL(prepared.blob); this.el.thumb.hidden = false;
      this.el.dropText.textContent = 'Photo ready — choose a different one?';
      this.park();
      this.status('');
    } catch (err) {
      this.track('upload_error');
      this.el.dropText.textContent = 'Choose a photo of your dog';
      this.status(err.message || 'The photo would not upload — try again.', true);
    } finally {
      this.el.drop.classList.remove('is-busy'); this.state.uploading=false; this.el.draw.disabled=false; this.syncPurchase();
    }
  }

  clearDesign() {
    this.pollEpoch++;
    this.state.design = null;
    if (this.state.pollTimer) clearTimeout(this.state.pollTimer);
    this.el.preview.hidden = true; this.el.preview.removeAttribute('src');
    this.el.placeholder.hidden = false;
    this.el.result.hidden = true;
    this.el.retitle.hidden = true;
    try { sessionStorage.removeItem('lig_om_design'); } catch { /* ignore */ }
  }

  async api(body) {
    const res = await fetch(`${this.mw}/api/ornament/design`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) { const e = new Error(data.error || `HTTP ${res.status}`); e.data = data; throw e; }
    return data;
  }

  async draw(again = false) {
    if (this.state.composing || this.state.cartBusy || this.state.uploading) return;
    const name = this.el.name.value.trim();
    if (!(again && this.state.design) && !this.state.photoUrl) { this.status('Choose a photo first.', true); this.el.file.click(); return; }
    if (!name) { this.status("What's your dog's name?", true); this.el.name.focus(); return; }
    const epoch = ++this.pollEpoch;
    this.el.draw.disabled = true; this.el.retry.disabled = true;
    this.showDrawing(again ? 'Drawing them again…' : 'Drawing your dog…');
    try {
      const data = again && this.state.design
        ? await this.api({ action: 'reroll', id: this.state.design.id })
        : await this.api({ action: 'create', photo_url: this.state.photoUrl, template: this.state.template, shape: this.state.shape, ...this.wordsBody(), hp_9f2: '' });
      if (epoch !== this.pollEpoch) return;
      this.track(again?'retry':'generate',{scene:this.state.template});this.generationStarted=Date.now();
      this.state.design = data.design || { id: data.id, status: 'queued' };
      this.remember({ ...this.state.design, template: this.state.template, shape: this.state.shape, name });
      this.poll(Date.now());
    } catch (err) {
      if (epoch !== this.pollEpoch) return;
      this.hideDrawing();
      this.status(err.message, true);
      if (err.data && err.data.handoff) { this.el.result.hidden = false; this.el.handoff.open = true; }
      this.el.draw.disabled = false; this.el.retry.disabled = false;
    }
  }

  async resume(id) {
    const epoch = this.pollEpoch;
    try {
      const r = await fetch(`${this.mw}/api/ornament/design?id=${encodeURIComponent(id)}`, { cache: 'no-store' });
      const data = await r.json();
      if (!r.ok || !data.design || epoch !== this.pollEpoch) return;
      this.state.design = data.design;
      const t = this.manifest.templates.find((x) => x.id === data.design.template);
      if (t) { this.pick(t.id); this.pickShape(data.design.shape); }
      this.use(data.design);
      return;
    } catch { /* a stale id is just a fresh start */ }
  }

  poll(startedAt) {
    if (!this.state.design) return;
    const id=this.state.design.id, epoch=this.pollEpoch;
    const active=()=>this.pollEpoch===epoch&&this.state.design?.id===id;
    this.state.pollTimer = setTimeout(async () => {
      if(!active())return;
      try {
        const r = await fetch(`${this.mw}/api/ornament/design?id=${encodeURIComponent(id)}`, { cache: 'no-store' });
        const data = await r.json();
        if(!active())return;
        if (r.ok && data.design) {
          this.state.design = data.design;
          if (data.design.status === 'ready') return this.showReady(data.design);
          if (data.design.status === 'failed') { this.hideDrawing(); this.status(data.design.error || 'The drawing did not come out — try again.', true); this.el.draw.disabled = false; this.el.retry.disabled = false; return; }
          const s = Math.round((Date.now() - startedAt) / 1000);
          this.el.drawingText.textContent = data.design.status==='queued' ? 'Your drawing is in the queue…' : s < 90 ? 'Drawing and checking your dog’s likeness…' : 'Still working. Your drawing is saved here; you can come back to this page.';
        }
      } catch { if(!active())return; /* keep polling */ }
      if(!active())return;
      if (Date.now() - startedAt > POLL_MAX_MS) { this.hideDrawing(); this.status('Your drawing is taking longer than usual. It is saved in Your drawings; reopen it to check progress.', true); this.el.draw.disabled = false; this.el.retry.disabled = false; return; }
      this.poll(startedAt);
    }, POLL_MS);
  }

  showDrawing(text) {
    this.el.add.disabled=true;
    this.el.drawing.hidden = false; this.el.drawingText.textContent = text; this.status('');
    this.el.stageTop.scrollIntoView({behavior:'smooth',block:'start'});
  }
  hideDrawing() { this.el.drawing.hidden = true; }

  showReady(design) {
    if(this.generationStarted){this.track('ready',{scene:design.template,ms:Date.now()-this.generationStarted});this.generationStarted=null;}
    this.state.design=design;
    this.hideDrawing();
    if(this.el.sample)this.el.sample.hidden=true;
    this.remember(design);
    if (this.el.save) { this.el.save.hidden = !this.state.saveEmail; if (design.saved) this.el.saveStatus.textContent = 'A link to this drawing has been emailed.'; }
    this.el.preview.alt=`${design.name} — your ornament preview`; this.el.preview.src = design.preview_url; this.el.preview.hidden = false; this.el.placeholder.hidden = true;
    this.el.result.hidden = false;
    this.el.retitle.hidden = true;
    const left = typeof design.tries_left === 'number' ? design.tries_left : 0;
    this.el.retry.textContent = left > 0 ? `Draw it again (${left} left)` : 'No tries left';
    this.el.retry.disabled = left <= 0 || design.editable===false;
    this.el.draw.disabled = false;
    this.el.draw.textContent = 'Start over with a new photo';
    this.status(design.ordered?'This edition has been ordered. Contact us for changes before printing.':`Your preview is ready. ${left} of 3 drawing attempts remaining for this drawing. Text and lettering changes do not use an attempt.`);
    this.syncPurchase();
    this.el.stageTop && this.el.stageTop.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }

  async recompose() {
    if (!this.state.design || this.state.design.status !== 'ready' || this.state.design.editable===false || this.state.cartBusy) return;
    this.pendingRecompose=true; this.syncPurchase();
    if(this.state.composing)return;
    this.state.composing=true; this.syncPurchase();
    try {
      while(this.pendingRecompose) {
        this.pendingRecompose=false;
        const d=this.state.design;
        this.showDrawing('Updating your preview…');
        const data=await this.api({action:'recompose',id:d.id,expected:d.updated_at,shape:this.state.shape,...this.wordsBody()});
        if(this.state.design?.id!==d.id)return;
        this.track('edit');this.state.design=data.design; this.showReady(data.design);
      }
    } catch(err) { this.pendingRecompose=false; this.hideDrawing(); this.status(err.message,true); }
    finally { this.state.composing=false; this.syncPurchase(); }
  }

  async addToCart() {
    let d = this.state.design;
    if (!d || d.status !== 'ready' || this.dirty() || this.state.composing || this.state.cartBusy || d.editable===false) return;
    this.state.cartBusy=true; this.syncPurchase();
    const controls=[...this.root.querySelectorAll('input,button,select')].map(el=>[el,el.disabled]);
    controls.forEach(([el])=>el.disabled=true);
    try { d=(await this.api({action:'snapshot',id:d.id,expected:d.updated_at})).design; }
    catch(err) { this.status(err.message,true); this.state.cartBusy=false; controls.forEach(([el,disabled])=>el.disabled=disabled); this.syncPurchase(); return; }
    const variant = this.variantFor(d.shape);
    if (!variant) { this.status('That shape is not available right now.', true); this.state.cartBusy=false; controls.forEach(([el,disabled])=>el.disabled=disabled); this.syncPurchase(); return; }
    const t = this.manifest.templates.find((x) => x.id === d.template) || { label: d.template };
    const shapeLabel = (this.manifest.shapes.find((x) => x.key === d.shape) || { label: d.shape }).label;
    const qty = Math.max(1, Math.min(10, parseInt(this.el.qty.value, 10) || 1));
    const dw = d.words || { top: '', name: d.name, bottom: d.line2 || '' };
    const wordsLine = [dw.top, dw.bottom].filter(Boolean).join(' · ');
    const styleLabel = d.style && Array.isArray(this.manifest.styles) ? (this.manifest.styles.find((x) => x.id === d.style) || {}).label || '' : '';
    const items = [{
      id: variant.id, quantity: qty,
      properties: { _design_id: d.id, Dog: d.name, Design: `${t.label} · ${shapeLabel}`, ...(wordsLine ? { Words: wordsLine } : {}), ...(styleLabel ? { Style: styleLabel } : {}), _preview: d.preview_url },
    }];
    const sections = this.cartSectionIds();
    this.el.add.disabled = true; this.el.add.setAttribute('aria-busy', 'true');
    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections, sections_url: window.location.pathname }),
      });
      const parsed = await response.json().catch(() => ({}));
      if (!response.ok || parsed.status) {
        document.dispatchEvent(new CartErrorEvent('lig-ornament-maker', parsed.message, parsed.description, parsed.errors));
        this.status(parsed.description || parsed.message || 'Could not add to the cart.', true);
        return;
      }
      const cart = await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json()).catch(() => null);
      document.dispatchEvent(new CartAddEvent(cart ?? parsed, 'lig-ornament-maker', { source: 'lig-ornament-maker', itemCount: cart?.item_count, sections: parsed.sections }));
      const drawer = document.querySelector('cart-drawer-component');
      if (drawer && typeof drawer.open === 'function') { try { drawer.open(); } catch { /* full-page cart */ } }
      this.track('cart',{scene:d.template});this.status('In your cart.');
    } catch {
      document.dispatchEvent(new CartErrorEvent('lig-ornament-maker', 'Could not add to cart', undefined, undefined));
      this.status('Could not add to the cart — try again.', true);
    } finally {
      this.state.cartBusy=false; controls.forEach(([el,disabled])=>el.disabled=disabled); this.el.add.removeAttribute('aria-busy'); this.syncPurchase();
    }
  }

  cartSectionIds() {
    const ids = new Set();
    document.querySelectorAll('cart-items-component[data-section-id]').forEach((node) => ids.add(node.getAttribute('data-section-id')));
    return Array.from(ids).join(',');
  }

  async handoff() {
    const d = this.state.design;
    const email = (this.el.hoEmail.value || '').trim();
    if (!d) { this.el.hoStatus.textContent = 'Draw it once first, so the designer can see what came out.'; return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email)) { this.el.hoStatus.textContent = 'That email does not look right.'; return; }
    this.el.hoSend.disabled = true;
    try {
      const response=await this.api({ action: 'handoff', id: d.id, email, note: (this.el.hoNote.value || '').trim() });
      if(response.design)this.state.design=response.design;
      this.el.hoStatus.textContent = 'Got it — a designer will draw it by hand and email you before anything prints.';
    } catch (err) { this.el.hoStatus.textContent = err.message; this.el.hoSend.disabled = false; }
  }
}

function esc(s) { return String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;'); }

function downscale(file) {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      try {
        const w = img.naturalWidth || img.width, h = img.naturalHeight || img.height;
        const scale = Math.min(1, DOWNSCALE_MAX_EDGE / Math.max(w, h));
        const canvas = document.createElement('canvas');
        canvas.width = Math.max(1, Math.round(w * scale)); canvas.height = Math.max(1, Math.round(h * scale));
        canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
        canvas.toBlob((blob) => resolve(blob ? { blob, contentType: 'image/jpeg', ext: 'jpg' } : rawFallback(file)), 'image/jpeg', DOWNSCALE_QUALITY);
      } catch { resolve(rawFallback(file)); }
    };
    img.onerror = () => { URL.revokeObjectURL(url); resolve(rawFallback(file)); }; // HEIC on a non-Safari browser: the middleware converts
    img.src = url;
  });
}
function rawFallback(file) {
  if (file.size > 12 * 1024 * 1024) return null;
  const ct = /^image\/(jpe?g|png|webp|heic|heif)$/i.test(file.type || '') ? file.type : 'image/jpeg';
  const ext = ((file.name || '').match(/\.(jpe?g|png|webp|heic|heif)$/i) || ['', 'jpg'])[1] || 'jpg';
  return { blob: file, contentType: ct, ext: ext.toLowerCase() };
}

document.querySelectorAll('[data-lig-om]').forEach((root) => new OrnamentMaker(root));

import(new URL(import.meta.url.includes('/assets/')?'lig-dog-gift-shared-dog.js':'../dog-maker/shared-dog.js',import.meta.url)).catch(()=>{});
